import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Box, Typography, Chip } from '@mui/material';
import { friendlyNameConverter } from '../../../../services/localizationService';
import { rateOfChange, filterByWindow } from '../statistics';
import { useTimeline } from '../TimelineContext';
import { useChart } from '../../../../components/chart';

export default function RateOfChangeWidget({ recordings = [], widgetConfig }) {
  const { currentTime, seek, window: timeWindow } = useTimeline();
  const recId = widgetConfig?.recId || '';
  const sensorKey = widgetConfig?.sensorKey || '';

  const rec = recordings.find((r) => r.id === recId);

  const rocData = useMemo(() => {
    if (!rec?.data?.[sensorKey]) return null;
    let points = rec.data[sensorKey];
    if (timeWindow) points = filterByWindow(points, timeWindow.start, timeWindow.end);
    const roc = rateOfChange(points);
    if (roc.length === 0) return null;
    return {
      labels: roc.map((dp) => dp.time),
      series: [{ type: 'line', name: 'Rate of Change', fill: 'solid', data: roc.map((dp) => dp.value) }],
    };
  }, [rec, sensorKey, timeWindow]);

  const sensorName = friendlyNameConverter(sensorKey || '');

  const chartOptions = useChart({
    fill: { type: ['solid'] },
    labels: rocData?.labels || [],
    xaxis: { type: 'datetime', min: timeWindow?.start, max: timeWindow?.end, labels: { datetimeUTC: false } },
    yaxis: { title: { text: `Δ ${sensorName} / sec` } },
    annotations: {
      xaxis: [{
        x: currentTime,
        borderColor: '#775DD0',
        label: { borderColor: '#775DD0', style: { color: '#fff', background: '#775DD0' }, text: 'Now' },
      }],
      yaxis: [{
        y: 0,
        borderColor: '#999',
        strokeDashArray: 4,
      }],
    },
    chart: {
      toolbar: { show: true, tools: { download: true, zoom: true, pan: true } },
      events: {
        markerClick: (_, __, opts) => {
          if (rocData?.labels?.[opts.dataPointIndex]) seek(rocData.labels[opts.dataPointIndex]);
        },
      },
    },
    stroke: { width: 1.5 },
    colors: ['#FF6B6B'],
  });

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {rocData ? (
        <Box sx={{ flex: 1, overflow: 'hidden', px: 1 }}>
          {timeWindow && <Chip label="Windowed" size="small" color="info" variant="outlined" sx={{ mb: 0.5, height: 18, fontSize: '0.6rem' }} />}
          <ReactApexChart type="line" series={rocData.series} options={chartOptions} height="90%" />
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">Select a recording and sensor</Typography>
        </Box>
      )}
    </Box>
  );
}
